import { QueryKey } from "@tanstack/react-query";
import { WSQueryCache, WSQueryClient } from "@ws-react-query";
import { SdkEndpointMethod } from "./createV3QueryHook";

/**
 * Whether `K` is a clean SDK endpoint method — excludes the generated `*Raw` /
 * `*RequestOpts` companions and any non-endpoint member. The base gate
 * {@link createV3Queries} applies before branching each method to its hook by verb.
 */
export type IsCleanEndpoint<TApi, K extends keyof TApi> = K extends
  | `${string}Raw`
  | `${string}RequestOpts`
  ? false
  : TApi[K] extends SdkEndpointMethod
    ? true
    : false;

/**
 * Builds a registry entry's cache key, mirroring the SDK request optionality.
 * Returns a `QueryKey` (always an array) so it feeds both `WSQueryCache` and the
 * raw react-query client without a cast.
 */
export type V3QueryKeyBuilder<TRequest> = undefined extends TRequest
  ? (request?: TRequest) => QueryKey
  : (request: TRequest) => QueryKey;

/**
 * Whether a method name is a read endpoint — a `get*` / `list*` / `search*`
 * query. The one boundary {@link createV3Queries} uses to split reads (query /
 * paginated query) from their exact complement, the mutations.
 */
export const isReadMethodName = (name: string): boolean =>
  /^(get|list|search)/.test(name);

/**
 * Names of an api instance's own prototype endpoint methods that satisfy
 * `isMatch`, excluding the generated `*Raw` / `*RequestOpts` companions and any
 * non-function members. Runtime counterpart of {@link IsCleanEndpoint} + the
 * caller's prefix gate.
 */
export const listApiMethodNames = (
  api: object,
  isMatch: (name: string) => boolean
): string[] => {
  const prototype = Object.getPrototypeOf(api);
  return Object.getOwnPropertyNames(prototype).filter(name => {
    if (name === "constructor") return false;
    if (name.endsWith("Raw") || name.endsWith("RequestOpts")) return false;
    if (!isMatch(name)) return false;
    return typeof (api as Record<string, unknown>)[name] === "function";
  });
};

/**
 * The cache helpers every registry read entry shares, bound to `baseKey` and the
 * endpoint `fetch`. The entry's key is `[...baseKey, request]`, and every helper
 * reads, seeds, or fetches that exact key — so all stay in lockstep with what the
 * generated hook reads.
 *
 * - `getQueryData` / `setQueryData` — synchronously read / seed the cached response.
 * - `getQueryError` / `setQueryError` — the error twin of the data pair:
 *   synchronously read / seed the cached error (the `WSQueryResult` `error`).
 *   `setQueryError` builds the query if it does not exist yet, so an error can be
 *   seeded before anything has been fetched.
 * - `fetchQueryData` / `forceFetchQueryData` — imperatively resolve the read,
 *   seeding the cache with the outcome (the response via `setQueryData`, or the
 *   error via `setQueryError`) and rejecting with the SDK error on failure.
 *   `fetchQueryData` is cache-first — a cached response short-circuits the endpoint
 *   call — while `forceFetchQueryData` always hits the endpoint and refreshes the
 *   cache.
 * - `queryKeyBase` is `baseKey` itself: the request-less partial key that, under
 *   `WSQueryCache`'s prefix-matching invalidation, targets *every* cached request
 *   variant of the method at once — the natural dependency key for a mutation that
 *   should refresh a whole list/search.
 */
export const buildCacheHelpers = (
  baseKey: unknown[],
  fetch: (request?: unknown) => Promise<unknown>
) => {
  const getQueryKey = (request?: unknown): QueryKey => [...baseKey, request];
  const setQueryData = (request: unknown, data: unknown): void => {
    WSQueryCache.setQueryData(getQueryKey(request), data);
  };
  const setQueryError = (request: unknown, error: unknown): void => {
    // React Query exposes no public error setter, so build-or-find the query and
    // patch its state — mirroring the `error` shape an observing `useQuery` sees.
    const query = WSQueryClient.getQueryCache().build(WSQueryClient, {
      queryKey: getQueryKey(request)
    });
    query.setState({
      status: "error",
      error,
      errorUpdatedAt: Date.now(),
      errorUpdateCount: query.state.errorUpdateCount + 1,
      fetchStatus: "idle"
    });
  };
  const getQueryData = (request?: unknown): unknown =>
    WSQueryCache.getQueryData(getQueryKey(request));
  const forceFetchQueryData = async (request?: unknown): Promise<unknown> => {
    try {
      const data = await fetch(request);
      setQueryData(request, data);
      return data;
    } catch (error) {
      setQueryError(request, error);
      throw error;
    }
  };
  return {
    getQueryKey,
    queryKeyBase: baseKey as QueryKey,
    getQueryData,
    setQueryData,
    getQueryError: (request?: unknown): unknown =>
      WSQueryClient.getQueryState<unknown, unknown>(getQueryKey(request))
        ?.error ?? undefined,
    setQueryError,
    forceFetchQueryData,
    fetchQueryData: async (request?: unknown): Promise<unknown> => {
      // Cache-first: a cached response short-circuits the endpoint call. The
      // response is always a defined object, so `undefined` reliably means "miss".
      const cached = getQueryData(request);
      if (cached !== undefined) {
        return cached;
      }
      return forceFetchQueryData(request);
    }
  };
};
